/**
 * Document blocks (Lexical root children) → Markdown, one block at a time
 * (git sync Stage 2, S2.1; the mapping is in Apiboo Documents › Git sync ›
 * "Git sync — Stage 2 plan"). Pure: media and page-link targets come from
 * `ctx`, so the sync layer decides paths and nothing secret is written.
 */
import { renderInline, escHtml } from './inline';
import { codeText } from './jsonNodes';
import { calloutToMarkdown } from './callout';

export const PAGE_MARK = '📄';
export const FALLBACK_LANG = 'apiboo-block';
/** `#apiboo-upload-<pendingRef>`: an image or video not uploaded yet (a harmless anchor on GitHub/GitLab). */
export const UPLOAD_ANCHOR = 'apiboo-upload-';

/** Block attributes Markdown has no syntax for, as an invisible comment line before the block. */
export function attrComment(node) {
  const parts = [];
  if (node.indent) parts.push(`indent=${node.indent}`);
  if (node.format) parts.push(`align=${node.format}`);
  return parts.length ? `<!-- apiboo ${parts.join(' ')} -->\n` : '';
}

/** @returns {string} Markdown for one block (no trailing blank line). */
export function blockToMarkdown(node, ctx, { htmlInline = false } = {}) {
  const inline = (children) => renderInline(children, ctx, htmlInline ? 'html' : 'md');
  switch (node.type) {
    case 'paragraph': {
      const kids = node.children || [];
      if (kids.length === 0) return `${attrComment(node)}<br>`;
      const embed = embedFromParagraph(kids);
      if (embed) return embed;
      return `${attrComment(node)}${inline(kids)}`;
    }
    case 'heading': {
      const level = Number(String(node.tag || 'h1').slice(1)) || 1;
      return `${attrComment(node)}${'#'.repeat(level)} ${inline(node.children)}`;
    }
    case 'quote':
      return `${attrComment(node)}${inline(node.children)
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n')}`;
    case 'callout':
      return `${attrComment(node)}${calloutToMarkdown(node.kind, inline(node.children))}`;
    case 'list':
      return listToMarkdown(node, ctx, '', htmlInline);
    case 'code': {
      const text = codeText(node);
      const runs = text.match(/`{3,}/g) || [];
      const fence = '`'.repeat(Math.max(3, ...runs.map((r) => r.length + 1)));
      return `${attrComment(node)}${fence}${node.language || ''}\n${text}\n${fence}`;
    }
    case 'table':
      return tableToMarkdown(node, ctx);
    case 'doc-image':
      return mediaToMarkdown(node, ctx);
    case 'doc-video':
      return `[▶ ${node.provider === 'vimeo' ? 'Vimeo' : 'YouTube'}](${watchUrl(node)})`;
    case 'page-link':
      return `${PAGE_MARK} ${renderInline([node], ctx, 'md')}`;
    default:
      return fallbackBlock(node);
  }
}

/**
 * The video's own page. youtube.com/watch keeps the start time (the app's
 * parser ignores ?t= on youtu.be links, so videoEmbedWatchUrl's short form
 * would lose it).
 */
function watchUrl(node) {
  if (node.provider === 'vimeo') return `https://vimeo.com/${node.videoId}`;
  return `https://www.youtube.com/watch?v=${node.videoId}${node.start > 0 ? `&t=${node.start}` : ''}`;
}

/**
 * Blocks' Markdown joined into one file. Two lists in a row with the same
 * marker family (bullet and checklist both use "-"; numbers use "1.") would
 * read back as ONE list, so an empty comment keeps them apart.
 */
export const LIST_SEPARATOR = '<!-- -->';
const markerFamily = (n) => (n.listType === 'number' ? 'number' : 'dash');
export function joinBlocks(nodes, parts) {
  return parts
    .map((p, i) => {
      const prev = nodes[i - 1];
      const next = nodes[i];
      const split = prev && next && prev.type === 'list' && next.type === 'list' && markerFamily(prev) === markerFamily(next);
      return split ? `${LIST_SEPARATOR}\n\n${p}` : p;
    })
    .join('\n\n');
}

/** The exact, always-correct form: the block's own JSON in a fenced block. */
export function fallbackBlock(node) {
  return `\`\`\`${FALLBACK_LANG}\n${JSON.stringify(withoutMediaKeys(node), null, 1)}\n\`\`\``;
}

/**
 * The block's JSON without any image's decryption key — wherever the image
 * sits (a paragraph, a list item, a table cell). Reading puts the key back
 * from the document as it is now (ctx.mediaFor, by src or pendingRef); a key
 * NEVER goes into a file (review of #372, 02.10.2026).
 */
export function withoutMediaKeys(node) {
  if (!node || typeof node !== 'object') return node;
  if (Array.isArray(node)) return node.map(withoutMediaKeys);
  const out = {};
  for (const [k, v] of Object.entries(node)) {
    if (k === 'k' && node.type === 'doc-image') continue;
    out[k] = typeof v === 'object' ? withoutMediaKeys(v) : v;
  }
  return out;
}

function embedFromParagraph(kids) {
  // An embed is written as its own block (doc-video); a paragraph is never turned into one here.
  return kids.length === 1 && kids[0].type === 'doc-video' ? blockToMarkdown(kids[0]) : null;
}

/* ── lists ─────────────────────────────────────────────────────────────── */

function listToMarkdown(list, ctx, pad, htmlInline) {
  const lines = [];
  let n = list.start || 1;
  let prevMarkerWidth = 2;
  for (const item of list.children || []) {
    const kids = item.children || [];
    if (kids.length === 1 && kids[0].type === 'list') {
      // Lexical keeps a nested list in its own wrapper item, under the previous item.
      lines.push(listToMarkdown(kids[0], ctx, `${pad}${' '.repeat(prevMarkerWidth)}`, htmlInline));
      n += 1; // the wrapper takes a number too (Lexical's own numbering)
      continue;
    }
    let marker;
    if (list.listType === 'number') marker = `${n}. `;
    else if (list.listType === 'check') marker = `- [${item.checked ? 'x' : ' '}] `;
    else marker = '- ';
    const width = list.listType === 'check' ? 2 : marker.length;
    const body = renderInline(kids, ctx, htmlInline ? 'html' : 'md');
    const cont = `${pad}${' '.repeat(width)}`;
    // An empty item is a bare marker: "- " with its trailing space reads back as a paragraph.
    lines.push(body ? `${pad}${marker}${body.split('\n').join(`\n${cont}`)}` : `${pad}${marker.trimEnd()}`);
    prevMarkerWidth = width;
    n += 1;
  }
  return lines.join('\n');
}

/* ── tables ────────────────────────────────────────────────────────────── */

const TABLE_KNOWN_KEYS = new Set(['type', 'version', 'children', 'direction', 'format', 'indent']);

function cellInlineOnly(cell) {
  const kids = cell.children || [];
  return kids.length === 1 && kids[0].type === 'paragraph' && !kids[0].indent && !kids[0].format;
}

/** A GitHub pipe table when it can say everything: a header row and nothing else special. */
function isPipeTable(table) {
  const rows = table.children || [];
  if (rows.length < 1) return false;
  if (Object.keys(table).some((k) => !TABLE_KNOWN_KEYS.has(k) && table[k] != null && !(Array.isArray(table[k]) && table[k].length === 0) && table[k] !== false && table[k] !== 0)) return false;
  const width = (rows[0].children || []).length;
  return rows.every((row, r) => {
    const cells = row.children || [];
    if (cells.length !== width) return false;
    return cells.every((c) =>
      (c.colSpan || 1) === 1 && (c.rowSpan || 1) === 1 && !c.backgroundColor && !c.width &&
      (r === 0 ? c.headerState === 1 : !c.headerState) && cellInlineOnly(c)
    );
  });
}

function tableToMarkdown(table, ctx) {
  if (isPipeTable(table)) {
    const rows = table.children.map((row) =>
      row.children.map((c) => renderInline(c.children[0].children, ctx, 'md').replace(/\n/g, '<br>') || ' ')
    );
    const line = (cells) => `| ${cells.join(' | ')} |`;
    return [line(rows[0]), line(rows[0].map(() => '---')), ...rows.slice(1).map(line)].join('\n');
  }
  return tableToHtml(table, ctx);
}

const tableAttrs = (table) => {
  const extra = {};
  for (const [k, v] of Object.entries(table)) if (!TABLE_KNOWN_KEYS.has(k) && v !== undefined) extra[k] = v;
  return Object.keys(extra).length ? ` data-apiboo="${escHtml(JSON.stringify(extra))}"` : '';
};

function tableToHtml(table, ctx) {
  const rows = (table.children || []).map((row) => {
    const cells = (row.children || []).map((c) => {
      const tag = c.headerState ? 'th' : 'td';
      const attrs = [];
      if ((c.colSpan || 1) !== 1) attrs.push(`colspan="${c.colSpan}"`);
      if ((c.rowSpan || 1) !== 1) attrs.push(`rowspan="${c.rowSpan}"`);
      if (c.backgroundColor) attrs.push(`style="background-color: ${escHtml(c.backgroundColor)}"`);
      if (c.headerState && c.headerState !== 1) attrs.push(`data-header="${c.headerState}"`);
      if (c.width) attrs.push(`width="${c.width}"`);
      const paras = (c.children || []).map((p) => {
        if (p.type !== 'paragraph') throw new Error('docMarkdown: a table cell holds a non-paragraph block');
        return `<p${p.indent ? ` data-indent="${p.indent}"` : ''}${p.format ? ` data-align="${escHtml(p.format)}"` : ''}>${renderInline(p.children, ctx, 'html')}</p>`;
      });
      return `    <${tag}${attrs.length ? ` ${attrs.join(' ')}` : ''}>${paras.join('')}</${tag}>`;
    });
    return `  <tr>\n${cells.join('\n')}\n  </tr>`;
  });
  return `<table${tableAttrs(table)}>\n${rows.join('\n')}\n</table>`;
}

/* ── media ─────────────────────────────────────────────────────────────── */

function mediaToMarkdown(node, ctx) {
  const path = ctx.mediaPath(node); // null = stays in Apiboo (too big for git, or not downloadable)
  const isVideo = String(node.mime || '').startsWith('video/');
  if (!path && !node.src && node.pendingRef) {
    // Still uploading, or the upload failed: a readable line, read back by its
    // upload ref — it used to fail the exact check and land in the .md as the
    // editor's raw JSON (Sandbox, card a876a48a, 03.10.2026). The next sync
    // after the upload writes the real file.
    return `[${isVideo ? '▶ Video' : '🖼 Image'}: ${escHtml(node.name || 'file')} — not uploaded yet](#${UPLOAD_ANCHOR}${node.pendingRef})`;
  }
  if (!path) {
    const mb = node.size ? ` · ${(node.size / 1048576).toFixed(1)} MB` : '';
    return `[${isVideo ? '▶ Video' : '🖼 Image'}: ${escHtml(node.name || 'file')}${mb} — kept in Apiboo](${node.src || ''})`;
  }
  if (isVideo) {
    return `<video src="${escHtml(path)}" controls${node.width ? ` width="${node.width}"` : ''}></video>`;
  }
  // ![alt](path) when the path survives a Markdown link as-is; brackets or a literal % in the
  // file name go in an <img> tag instead (exact, and still readable).
  if (!node.width && !/[[\]]/.test(node.alt || '') && !/[[\]%]/.test(path)) {
    return `![${node.alt || ''}](${path.replace(/[ ()]/g, encodeURIComponent)})`;
  }
  return `<img src="${escHtml(path)}" alt="${escHtml(node.alt || '')}"${node.width ? ` width="${node.width}"` : ''}>`;
}
